import type {
  BulkActionResult,
  BulkFailure,
  BulkFailureReason,
} from "@/lib/api/clients/EventClient";

/**
 * The single seam between the server's bulk outcome and everything that reads
 * it. Callers never touch `failedIds`/`failures` directly, so when the server
 * contract moves only this file changes.
 *
 * Why it exists: `failed > 0` used to mean four unrelated things, and nothing
 * distinguished them. Two of them ("you may not", "billing blocks it") leave
 * the row in place; two ("someone else deleted it", "it is gone but a later
 * step errored") do not. The provider nevertheless dropped every selected row
 * from local state, so a blocked delete looked like a successful one until the
 * next refetch put the row back.
 */

export type NormalizedBulkOutcome = {
  processed: number;
  failed: number;
  failures: BulkFailure[];
};

export type BulkSplit = {
  /** Rows the server confirms are gone — safe to drop locally. */
  goneIds: Set<string>;
  /** Rows that are still there — must stay in the list. */
  keptIds: Set<string>;
  /**
   * True when at least one failure carried no reason we understand, so local
   * state cannot be trusted and the caller should refetch.
   */
  needsServerReconcile: boolean;
};

/**
 * Every per-row reason the backend's bulk lanes are known to send
 * (EventService batch delete / archive / un-archive). The source of truth for
 * "has copy" checks — the backend may pass any other IllegalArgument code
 * through verbatim, which `describeBulkFailures` then looks up in `errors.*`.
 */
export const KNOWN_BULK_REASONS: ReadonlySet<string> = new Set([
  "insufficientCapability",
  "activeSubscription",
  "blocked",
  "raceLost",
  "cascadeFailed",
  "eventQuotaExceeded",
  "eventPermanentlyArchived",
]);

/** Reasons that mean the row is really gone. Everything else keeps the row. */
const GONE_REASONS = new Set<string>(["raceLost", "cascadeFailed"]);
/** Reasons we understand that mean the row survived. */
const KEPT_REASONS = new Set<string>(["insufficientCapability"]);

/**
 * Accepts the raw response (or `undefined` when an action was skipped) and
 * fills in `failures` from `failedIds` when the server does not send it yet.
 */
export function normalizeBulkOutcome(
  raw: BulkActionResult | undefined,
): NormalizedBulkOutcome | undefined {
  if (!raw || typeof raw !== "object") return undefined;

  const processed = typeof raw.processed === "number" ? raw.processed : 0;
  const failed = typeof raw.failed === "number" ? raw.failed : 0;

  if (Array.isArray(raw.failures) && raw.failures.length > 0) {
    return { processed, failed, failures: raw.failures };
  }

  const failedIds = Array.isArray(raw.failedIds) ? raw.failedIds : [];
  return {
    processed,
    failed,
    failures: failedIds.map((id) => ({ id })),
  };
}

/**
 * Splits the failures into "gone" and "kept".
 *
 * An unknown or missing reason keeps the row and asks for a refetch. That
 * direction is deliberate: a row wrongly left behind is corrected by the next
 * load, while a row wrongly removed HIDES an event that still exists — which
 * is the bug this replaces. It also means a future fifth reason code degrades
 * safely instead of silently deleting rows from the UI.
 */
export function splitFailedIds(
  outcome: NormalizedBulkOutcome | undefined,
  attemptedIds: string[],
): BulkSplit {
  const goneIds = new Set<string>();
  const keptIds = new Set<string>();
  let needsServerReconcile = false;

  for (const failure of outcome?.failures ?? []) {
    const reason: BulkFailureReason | undefined = failure.reason;
    if (reason && GONE_REASONS.has(reason)) {
      goneIds.add(failure.id);
      continue;
    }
    keptIds.add(failure.id);
    if (!reason || !KEPT_REASONS.has(reason)) needsServerReconcile = true;
  }

  // Anything we attempted and the server did not report as failed succeeded.
  for (const id of attemptedIds) {
    if (!keptIds.has(id)) goneIds.add(id);
  }

  return { goneIds, keptIds, needsServerReconcile };
}
